import axios from "axios";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  ExperienceActionRequestBodyType,
  ExperienceActionRequestPathParamsType,
  ExperienceActionResponseBodyType,
} from "@intelligems/ig-types/functions/experiences/experience-action";
import { ExperienceCategory } from "@intelligems/ig-types/dtos/experience-categories";
import { TabEnumFe, TabType } from "@intelligems/ig-types/fe-only/experiences";
import { officialCategoryName } from "@intelligems/ig-utils/common";
import { maybeOpenEarlyTestReport } from "@src/features/experiments/components/earlyTestGuard/maybeOpenEarlyTestReport";
import { getMutationErrorMessage } from "@src/queryClient";
import { initExperimentFromDto } from "@src/store/slices/experiences";
import { setIgnoreUpdateDetected } from "@src/store/slices/global";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  experienceKeys,
  historyKeys,
  invalidateModuleExperienceListQueries,
  offerKeys,
  scheduleKeys,
  setExperienceQueryData,
  updateInExperiencesCache,
  updateInExperiencesListCache,
} from "../queryKeys";
import { ApiRequest } from "../types/api";

import { experienceAction } from "./experience-action";

export const usePauseExperience = (category: ExperienceCategory) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  return useMutation<
    ExperienceActionResponseBodyType,
    Error,
    ApiRequest<
      ExperienceActionRequestBodyType,
      ExperienceActionRequestPathParamsType & {
        name: string;
      }
    >
  >({
    mutationKey: ["pauseExperience"],
    onMutate: () => {
      dispatch(setIgnoreUpdateDetected(true));
    },
    mutationFn: async (data) => experienceAction(data),
    onSuccess: async (data, payload) => {
      void queryClient.invalidateQueries({
        queryKey: historyKeys.one(payload.pathParams.experienceId),
      });
      invalidateModuleExperienceListQueries(queryClient);

      if (data.experience) {
        // Set the specific experience cache
        setExperienceQueryData(queryClient, data.experience);

        if (data.experience.category === "experiment") {
          // Invalidate experiment list
          void queryClient.invalidateQueries({
            queryKey: experienceKeys.experimentList,
          });
        } else if (data.experience.category === "personalization") {
          // Update personalization list caches in-place (no refetch)
          updateInExperiencesCache(
            queryClient,
            experienceKeys.detailedPersonalizationList,
            data.experience
          );
          updateInExperiencesListCache(
            queryClient,
            experienceKeys.homeOverviewPersonalizationList,
            data.experience
          );

          // Update offer-related caches if this is an offer experience
          if (data.experience.testTypes.hasTestCampaign) {
            updateInExperiencesCache(
              queryClient,
              experienceKeys.offerPersonalizationList,
              data.experience
            );
            void queryClient.invalidateQueries({ queryKey: offerKeys.all });
            void queryClient.invalidateQueries({
              queryKey: offerKeys.experiencesList,
            });
          }

          // Update checkout-related caches if this is a checkout experience
          if (data.experience.testTypes.hasTestCheckoutBlocks) {
            updateInExperiencesCache(
              queryClient,
              experienceKeys.checkoutPersonalizationList,
              data.experience
            );
          }

          // Update post-purchase-related caches if this is a post-purchase experience
          if (data.experience.testTypes.hasTestPostPurchase) {
            updateInExperiencesCache(
              queryClient,
              experienceKeys.postPurchasePersonalizationList,
              data.experience
            );
          }
        }

        await queryClient.invalidateQueries({
          queryKey: scheduleKeys.one(data.experience.id),
        });

        dispatch(
          initExperimentFromDto({
            experience: data.experience,
            testFlowType: data.experience.type,
            tab: (searchParams.get("tab") as TabType) ?? TabEnumFe.ANALYTICS,
          })
        );
      }

      maybeOpenEarlyTestReport(dispatch, data.experience, "pause");
    },
    onError: (error) => {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 503) {
          toast.info(`Pausing ${officialCategoryName(category, true)}...`);
        } else {
          const errorDetail = getMutationErrorMessage(error);
          toast.error(
            `Error pausing ${officialCategoryName(category, true)} ${errorDetail ? `: ${errorDetail}` : ""}`
          );
        }
      }
    },
  });
};
